'use client'

import { Event, Meta, Tag } from '@/types'
import { LuFilter } from 'react-icons/lu'
import { Badge } from 'flowbite-react'
import Sort from '@/app/web/events/ui/Sort'
import EventList from '@/app/web/events/ui/EventList'
import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { fetchEvents } from '@/app/web/events/actions'
import {
  Accordion,
  AccordionPanel,
  Loading,
  MultipleFilter,
} from '@/components/common'
import { compact, isEmpty, snakeCase } from 'lodash'

const EVENT_FORMATS = {
  virtual: 'Virtual',
  in_person: 'In-person',
  hybrid: 'Hybrid',
}

const DEFAULT_STATE = 'register_now'

const ListingContainer = ({ tags }: { tags?: Tag[] }) => {
  const searchParams = useSearchParams()
  const [events, setEvents] = useState<Event[]>([])
  const [pagy, setPagy] = useState<Meta | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [hideFilter, setHideFilter] = useState<boolean>(true)
  const [filterItems, setFilterItems] = useState<number>(0)

  useEffect(() => {
    const params = new URLSearchParams(searchParams)
    if (params.has('page')) {
      params.set('page', '1')
      window.history.replaceState(null, '', `?${params.toString()}`)
    }
  }, [])

  useEffect(() => {
    setIsLoading(true)
    const currentPage = searchParams.get('page')
    fetchEventData().then((res) => {
      const hackathonOnly = res.data.filter(
        (e) => e.event_type !== 'bug_bounty'
      )
      setEvents((prevEvents) =>
        currentPage && currentPage !== '1'
          ? [...prevEvents, ...hackathonOnly]
          : hackathonOnly
      )

      setPagy(res.meta)
      setIsLoading(false)
    })
  }, [searchParams])

  const fetchEventData = async () => {
    const keyword = searchParams.get('keyword') || ''
    const tags = searchParams.get('tags') || ''
    const categories = searchParams.get('categories') || ''
    const state = searchParams.get('state') || DEFAULT_STATE
    const orderBy = searchParams.get('orderBy') || ''
    const page = Number(searchParams.get('page')) || 1

    setFilterItems(
      compact(categories?.split(',')).length + compact(tags?.split(',')).length
    )

    return await fetchEvents(keyword, tags, categories, state, orderBy, page)
  }

  const handleClearFilter = () => {
    const params = new URLSearchParams(searchParams)
    params.delete('tags')
    params.delete('categories')

    window.history.replaceState(null, '', `?${params.toString()}`)
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-4">
          <button
            onClick={() => setHideFilter(!hideFilter)}
            className={`${
              hideFilter && filterItems === 0
                ? 'bg-white'
                : 'bg-gray-800 text-white'
            } flex items-center gap-3 border border-gray-200 text-sm px-5 py-2.5 rounded-lg hover:bg-gray-800 hover:text-white`}
          >
            <LuFilter className="h-5 w-5" />
            <span className="text-lg">Filter</span>
            {filterItems !== 0 && (
              <Badge className="rounded-xl bg-gray-600 text-xs text-gray-100">
                {filterItems}
              </Badge>
            )}
          </button>
          {pagy && events.length !== 0 ? (
            <span>{events.length} hackathons found</span>
          ) : (
            pagy && <span>No Matching Results</span>
          )}
        </div>
        <Sort />
      </div>
      <div className="flex items-start justify-start gap-6 flex-col md:flex-row">
        <div
          className={`${
            hideFilter && 'hidden'
          } border rounded-lg p-3 shadow top-0 left-0 z-10 bg-white w-full md:w-72 md:min-w-72`}
        >
          <div className="w-full inline-flex items-center justify-between mb-3">
            <span className="text-gray-500 text-sm font-medium">Filters</span>
            <div
              className="text-gray-500 text-sm font-medium cursor-pointer"
              onClick={() => handleClearFilter()}
            >
              Clear all
            </div>
          </div>
          <Accordion>
            <AccordionPanel title="Team Status">
              <MultipleFilter
                data={EVENT_FORMATS}
                defaultItem={3}
                keyAsVal={true}
                filterType="categories"
              />
            </AccordionPanel>
            <AccordionPanel title="Roles/Expertise Areas">
              {tags && (
                <MultipleFilter
                  data={tags.reduce(
                    (a, v) => ({ ...a, [snakeCase(v.name)]: v.name }),
                    {}
                  )}
                  defaultItem={7}
                  filterType="tags"
                />
              )}
            </AccordionPanel>
          </Accordion>
        </div>
        <EventList
          events={events}
          isExpand={hideFilter}
          pagy={pagy}
          state={searchParams.get('state') || ''}
        />
      </div>
      {isLoading && <Loading />}
    </>
  )
}

export default ListingContainer
